import * as THREE from "three";
import { cloneModel, loadModel } from "./ModelLoader";

export type AnimState = "idle" | "walk" | "run" | "attack" | "jump";
type LoopState = Exclude<AnimState, "attack">;

/**
 * Clip names inside Kenney's "Blocky Characters" GLB (CC0 — see ASSETS.md).
 * That pack has no dedicated jump clip, so jump reuses idle for the upper
 * body while PlayerController's physics does the actual vertical motion.
 */
const CLIP_NAMES: Record<AnimState, string> = {
  idle: "idle",
  walk: "walk",
  run: "sprint",
  attack: "attack-melee-right",
  jump: "idle",
};

const ATTACK_CROSSFADE = 0.05;
const LOCOMOTION_CROSSFADE = 0.15;

// The source GLB is ~2.7 units tall; scale down to match the arena's human-scale
// calibration (camera height, target marker height, arena proportions, etc.).
const MODEL_SCALE = 0.7;

/**
 * Character rig backed by a real animated GLB (swap the URL to re-skin).
 * Loading is asynchronous — `root` exists immediately so callers can add it
 * to the scene right away; the model fades in once loaded.
 */
export class CharacterModel {
  readonly root = new THREE.Group();

  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<AnimState, THREE.AnimationAction>();
  private currentAction: THREE.AnimationAction | null = null;
  private locomotionState: LoopState = "idle";
  private attacking = false;
  private ready = false;

  constructor(modelUrl: string) {
    loadModel(modelUrl)
      .then((loaded) => this.onLoaded(cloneModel(loaded)))
      .catch((err) => console.error(`[CharacterModel] failed to load ${modelUrl}`, err));
  }

  private onLoaded({ scene, animations }: { scene: THREE.Group; animations: THREE.AnimationClip[] }): void {
    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if ((mesh as THREE.SkinnedMesh).isSkinnedMesh || mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    scene.scale.setScalar(MODEL_SCALE);
    this.root.add(scene);

    this.mixer = new THREE.AnimationMixer(scene);
    this.mixer.addEventListener("finished", (e) => {
      if (e.action === this.actions.get("attack")) {
        this.attacking = false;
        this.playLoop(this.locomotionState);
      }
    });

    for (const [state, clipName] of Object.entries(CLIP_NAMES) as [AnimState, string][]) {
      const clip = THREE.AnimationClip.findByName(animations, clipName);
      if (clip) this.actions.set(state, this.mixer.clipAction(clip));
    }

    this.ready = true;
    this.playLoop(this.locomotionState);
  }

  setState(state: AnimState): void {
    if (state === "attack") {
      this.playAttack();
      return;
    }
    if (this.locomotionState === state) return;
    this.locomotionState = state;
    if (!this.attacking) this.playLoop(state);
  }

  private playLoop(state: LoopState): void {
    if (!this.ready) return;
    const action = this.actions.get(state) ?? this.actions.get("idle");
    if (!action || action === this.currentAction) return;
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.play();
    if (this.currentAction) action.crossFadeFrom(this.currentAction, LOCOMOTION_CROSSFADE, false);
    this.currentAction = action;
  }

  private playAttack(): void {
    if (!this.ready) return;
    const action = this.actions.get("attack");
    if (!action) return;
    this.attacking = true;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = false;
    action.play();
    if (this.currentAction && this.currentAction !== action) {
      action.crossFadeFrom(this.currentAction, ATTACK_CROSSFADE, false);
    }
    this.currentAction = action;
  }

  update(dt: number): void {
    this.mixer?.update(dt);
  }

  dispose(): void {
    this.mixer?.stopAllAction();
    this.root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      mesh.geometry?.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    });
  }
}
